import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { RefreshCwIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ChatPanelWithProvider } from './components/chat/ChatPanelWithProvider';
import { ActivityPanelContainer } from './components/activity-panel/ActivityPanelContainer';
import { ProductModalContainer } from './components/activity-panel/panels/constraints/components/content/contentComponents/ProductModalContainer';
import { AssistantNameSelector } from 'shared/components/AssistantNameSelector';
import colorsStore from 'shared/store/colorsStore';
import uiStore from 'shared/store/uiStore';
import useAppStatus from 'shared/store/appStatusStore';
import { useTabletAndBelowMediaQuery } from 'shared/store/responsiveStore';
import constraintProfilesStore from 'shared/store/constraintProfilesStore';

import mainStyles from 'style/main.module.scss';
import styles from './assistantPage.module.scss';

const AssistantPageContent: React.FC = () => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const menuVisible = uiStore((state) => state.menuVisible);
  const isMenuClosing = uiStore((state) => state.isMenuClosing);
  const appStatus = useAppStatus((state) => state.appStatus);
  const isTabletOrBelow = useTabletAndBelowMediaQuery();
  // Block all interaction with the chat input and the Vincoli pane until the
  // session-startup loader has populated the profile catalog + predefiniti.
  // Otherwise the user could edit a constraint or send a message against a
  // null workingDocument (no default-on-start has fired yet), producing a
  // race where edits are lost or the agent receives a thread without
  // constraints metadata.
  const sessionLoaded = constraintProfilesStore((s) => s.loaded);
  const { t } = useTranslation();

  const [isActivityPanelClosing, setIsActivityPanelClosing] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check if activity panel should be expanded
  const isActivityPanelOpen = appStatus !== 'idle';

  // For desktop: always show the activity panel container
  // For mobile: show it when menu is visible OR when activity panel is expanded
  const shouldShowActivityPanel = !isTabletOrBelow || menuVisible;

  // Handle closing animation for mobile activity panel
  useEffect(() => {
    if (isTabletOrBelow && isActivityPanelOpen && !menuVisible) {
      // Activity panel is open but menu is closing - start closing animation
      setIsActivityPanelClosing(true);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setIsActivityPanelClosing(false);
      }, 300); // Match animation duration
    } else {
      setIsActivityPanelClosing(false);
    }
  }, [isTabletOrBelow, isActivityPanelOpen, menuVisible]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className={mainStyles.mainContainer}
      style={{
        backgroundColor: colorsConfig.white,
        position: 'relative',
      }}
    >
      {!sessionLoaded && (
        // Fully opaque loading screen kept on until the session-startup data
        // is ready. It covers the chat panel completely so the chat's own
        // transient "Connessione in corso" indicator is hidden underneath
        // (no doubled indicators) and the screen stays put until all four
        // session fetches (constraint-profiles × 2, predefiniti × 2)
        // resolve — preventing the user from sending a message or editing
        // constraints before the working document is initialised.
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1000,
            backgroundColor: colorsConfig.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            color: '#666',
          }}
        >
          <RefreshCwIcon className="size-5 animate-spin" />
          <span>{t('savedProfiles.loading')}</span>
        </div>
      )}
      <div className="flex h-full flex-col">
        <div className={styles.floatingDropdown}>
          <AssistantNameSelector />
        </div>

        <div className="relative flex h-full flex-1 flex-row flex-nowrap">
          {shouldShowActivityPanel && (
            <div
              className={classNames(styles.activityPanelContainer, {
                [styles.activityPanelContainerHiding]: isTabletOrBelow && (isMenuClosing || isActivityPanelClosing),
              })}
            >
              <div className={styles.activityPanelInnerContainer}>
                <ActivityPanelContainer />
              </div>
            </div>
          )}

          {/* RootLayout sizes the page to its content (min-width: fit-content); without the
              containment the chat's charts and tables kept their width when the side panel
              opened and pushed the composer off-screen. Now the chat takes what the panel leaves. */}
          <div
            className="h-full max-h-full w-full min-w-0 max-w-full flex-grow [contain:inline-size]"
            style={{
              backgroundColor: colorsConfig.white,
            }}
          >
            <ChatPanelWithProvider />
          </div>
        </div>
      </div>
    </div>
  );
};

export const AssistantPage: React.FC = () => {
  return (
    <>
      <AssistantPageContent />
      {/* Render modal at top level to survive responsive layout changes */}
      <ProductModalContainer />
    </>
  );
};
